import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import api from '../services/api';
import { useInsufficientCredits } from '../contexts/InsufficientCreditsContext';

/**
 * Custom hook for managing contact enrichment
 * Handles enriching contacts and managing enrichment state
 * 
 * @param {string} campaignId - Campaign ID
 * @param {string} versionId - Active version ID
 * @param {Function} onEnrichmentComplete - Callback when enrichment completes
 * @param {Function} withCreditWarning - Optional credit warning wrapper function
 * @param {Object} listRef - Optional ref to the virtualized list for scroll control
 * @returns {Object} - Enrichment state and operations
 */
const useContactEnrichment = (campaignId, versionId, onEnrichmentComplete, withCreditWarning = null, listRef = null) => {
  const [isEnriching, setIsEnriching] = useState(false);
  const toast = useToast();
  const { showInsufficientCreditsModal } = useInsufficientCredits();

  /**
   * Enrich contacts
   * 
   * @param {Array<string>} contactIds - Optional array of elasticsearch_ids to enrich.
   *                                     If null, enriches all unenriched contacts.
   */
  const enrichContacts = useCallback(async (contactIds = null) => {
    if (!campaignId || !versionId) {
      toast({
        title: 'Error',
        description: 'Campaign or version information is missing',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Define the actual enrichment action
    const performEnrichment = async () => {
      try {
        const response = await api.post(
          `/api/enrichment/campaigns/${campaignId}/contacts/enrich`,
          {
            version_id: versionId,
            contact_ids: contactIds
          }
        );

        // Check if this is a 403 signup-required response (API interceptor resolves instead of rejects)
        if (response.status === 403 || response.requiresSignup) {
          // Signup modal already triggered by API interceptor, don't show toast
          return;
        }

        const { contacts_enriched, credits_charged, remaining_credits } = response.data;

        toast({
          title: 'Contacts Enriched',
          description: `Successfully enriched ${contacts_enriched} contacts`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Dispatch event to update header credit count
        window.dispatchEvent(new Event('enrichmentCompleted'));

        // Call completion callback to refresh data
        if (onEnrichmentComplete) {
          await onEnrichmentComplete();
          
          // Scroll to top after enrichment completes
          if (listRef?.current) {
            setTimeout(() => {
              listRef.current?.scrollToItem?.(0, 'start');
            }, 0);
          }
        }

        return response.data;
      } catch (error) {
        console.error('Error enriching contacts:', error);
        
        // Handle insufficient credits error (402) - show modal instead of toast
        if (error.response?.status === 402) {
          const errorDetail = error.response?.data?.detail || 'You do not have enough credits to enrich these contacts.';
          showInsufficientCreditsModal(errorDetail);
        } 
        // Handle trial user restriction (403) - signup modal is shown by API interceptor, no toast
        else if (error.response?.status === 403) {
          // Silently handle - signup modal will be shown by the API interceptor
        }
        else {
          // For other errors, show toast
          toast({
            title: 'Enrichment Failed',
            description: error.response?.data?.detail || 'Failed to enrich contacts',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }

        throw error;
      }
    };

    // Show spinner immediately — covers both credit-check phase and actual enrichment
    setIsEnriching(true);
    try {
      if (withCreditWarning) {
        return await withCreditWarning(
          'contact_enrichment',
          {
            campaign_id: campaignId,
            version_id: versionId,
            contact_ids: contactIds
          },
          performEnrichment
        );
      } else {
        return await performEnrichment();
      }
    } finally {
      setIsEnriching(false);
    }
  }, [campaignId, versionId, toast, onEnrichmentComplete, showInsufficientCreditsModal, withCreditWarning]);

  return {
    isEnriching,
    enrichContacts,
  };
};

export default useContactEnrichment;
